import type { NormalizeOAS, OASOutput, OASRequestParams } from 'fets';
import type { ApiClient } from '../fetsClient';
import type openapi from '../openapi';
import { SubscriptionTier } from '../../types/Organization';

type O = NormalizeOAS<typeof openapi>;
type RequestParams = OASRequestParams<O, '/api/subscription/details', 'post'>;
type ResponseBody = OASOutput<O, '/api/subscription/details', 'post'>;

type Params = {
  returnUrl: string;
};

type Invoice = {
  amountPaid: number | bigint;
  invoicePdf: string;
  created: string;
  currency: string;
  status: string;
};

type SubscriptionPage = {
  pricingPlanCategory: SubscriptionTier;
  price: number | bigint | null;
  currency: string | null;
  nextBillingDate: string | null;
  seats: number | bigint | null;
  lastFourDigits: string | null;
  creditCardBrand: string | null;
  billingAddress: string | null;
  taxId: string | null;
  invoices: Invoice[] | null;
  downgradeScheduled: boolean | null;
};

type Result = {
  billingPortal: string;
  subscriptionDetails: SubscriptionPage;
};

export const getSubscriptionPageFactory = (apiClient: ApiClient) => {
  const getSubscriptionPage = async (variables: Params) => {
    const requestBody = mapInputToRequestBody(variables);
    const response = await apiClient['/api/subscription/details'].post(requestBody);
    const responseBody = await response.json();

    return mapResponseToResult(responseBody);
  };

  const mapInputToRequestBody = (input: Params): RequestParams => {
    return {
      json: {
        returnUrl: input.returnUrl,
      },
    };
  };

  const mapResponseToResult = (response: ResponseBody): Result => {
    const details = response.subscriptionDetails;
    return {
      billingPortal: response.billingPortal,
      subscriptionDetails: {
        pricingPlanCategory: details.pricingPlanCategory,
        price: details.price ?? null,
        currency: details.currency ?? null,
        nextBillingDate: details.nextBillingDate ?? null,
        seats: details.seats ?? null,
        lastFourDigits: details.lastFourDigits ?? null,
        creditCardBrand: details.creditCardBrand ?? null,
        billingAddress: details.billingAddress ?? null,
        taxId: details.taxId ?? null,
        invoices: details.invoices ?? [],
        downgradeScheduled: details.downgradeScheduled ?? null,
      },
    };
  };

  return getSubscriptionPage;
};
